import { Client, type IMessage } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { useEffect, useRef } from "react";
import { WS_PATH } from "@/shared/config/env";
import { useAuthStore } from "@/shared/auth/authStore";
import { ensureFreshToken } from "@/shared/api/httpClient";
import { useWsStore } from "./wsStore";
import {
  WS_BUS_DESTINATIONS,
  WS_DEST_PHONE_STATUS,
  WS_DEST_SUPERVISORS,
  WS_DEST_USER_PHONE_STATUS,
  WS_PRIVILEGED_DESTINATIONS,
  WS_PRIVILEGED_ROLES,
  emitWsDestination,
} from "./wsBus";

export function usePhoneManagerWs(enabled: boolean) {
  const clientRef = useRef<Client | null>(null);
  const loopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled || !useAuthStore.getState().isAuthenticated()) {
      return;
    }

    let disposed = false;

    const disconnect = () => {
      if (loopRef.current) {
        clearTimeout(loopRef.current);
        loopRef.current = null;
      }
      clientRef.current?.deactivate();
      clientRef.current = null;
      useWsStore.getState().resetRealtime();
    };

    const scheduleLoop = () => {
      loopRef.current = setTimeout(async () => {
        if (disposed) return;
        if (!useAuthStore.getState().isAuthenticated()) {
          disconnect();
          return;
        }
        await ensureFreshToken();
        const client = clientRef.current;
        if (!client?.connected) {
          disconnect();
          connect();
          return;
        }
        scheduleLoop();
      }, 5000);
    };

    const connect = async () => {
      const token = await ensureFreshToken();
      if (!token || disposed) return;

      const client = new Client({
        webSocketFactory: () => new SockJS(WS_PATH) as WebSocket,
        connectHeaders: { Authorization: `Bearer ${token}` },
        reconnectDelay: 1000,
        onConnect: () => {
          useWsStore.getState().setStatusOnline(true);
          const privileged = useAuthStore.getState().includeAnyRole(WS_PRIVILEGED_ROLES);
          // Estado do painel: /topic/phoneStatus (incrementais + broadcast legado do noMemory em prod)
          // e /user/queue/phoneStatus (snapshot do noMemory só para esta sessão, backend novo).
          client.subscribe(WS_DEST_PHONE_STATUS, onStompMessage);
          client.subscribe(WS_DEST_USER_PHONE_STATUS, onStompMessage);
          // Supervisores: tópico restrito (o guard do backend rejeita USER).
          if (privileged) client.subscribe(WS_DEST_SUPERVISORS, onStompMessage);
          client.subscribe("/topic/greetings", onStompMessage);
          // Destinos extras (screen pop do discador, stats de campanhas) → barramento.
          for (const dest of WS_BUS_DESTINATIONS) {
            if (WS_PRIVILEGED_DESTINATIONS.has(dest) && !privileged) continue;
            client.subscribe(dest, (m) => {
              if (m.body) emitWsDestination(dest, m.body);
            });
          }
          client.publish({
            destination: "/app/noMemory",
            headers: { Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: "noMemory" }),
          });
          scheduleLoop();
        },
        onStompError: () => {
          useWsStore.getState().setStatusOnline(false);
        },
        onWebSocketClose: () => {
          useWsStore.getState().setStatusOnline(false);
        },
      });

      function onStompMessage(message: IMessage) {
        if (!message.body) return;
        try {
          const body = JSON.parse(message.body);
          useWsStore.getState().handleMessage(body);
        } catch {
          /* ignore malformed */
        }
      }

      clientRef.current = client;
      client.activate();
    };

    connect();

    return () => {
      disposed = true;
      disconnect();
    };
  }, [enabled]);
}
